import { useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import type { Json } from '@/integrations/supabase/types';

export const useActivityLogger = () => {
  const { user, username } = useAuth();

  const logActivity = useCallback(
    async (
      eventType: string,
      eventCategory: string,
      description?: string,
      metadata?: Record<string, unknown>,
      success: boolean = true
    ) => {
      if (!user) return;

      try {
        await supabase.rpc('log_audit_event', {
          _user_id: user.id,
          _username: username || 'unknown',
          _event_type: eventType,
          _event_category: eventCategory,
          _description: description || null,
          _metadata: (metadata || {}) as Json,
          _success: success,
          _user_agent: navigator.userAgent,
        });
      } catch (error) {
        // Silently fail to avoid disrupting user workflows
        console.error('Activity log failed:', error);
      }
    },
    [user, username]
  );

  return { logActivity };
};

/**
 * Standalone function for logging activity outside of React components (e.g. useAuth).
 * Requires explicit userId and username since hooks aren't available.
 */
export const logActivityDirect = async (
  userId: string,
  username: string,
  eventType: string,
  eventCategory: string,
  description?: string,
  metadata?: Record<string, unknown>,
  success: boolean = true
) => {
  try {
    await supabase.rpc('log_audit_event', {
      _user_id: userId,
      _username: username,
      _event_type: eventType,
      _event_category: eventCategory,
      _description: description || null,
      _metadata: (metadata || {}) as Json,
      _success: success,
      _user_agent: navigator.userAgent,
    });
  } catch (error) {
    console.error('Activity log failed:', error);
  }
};
